import { Box, Heading, Paragraph, Spinner } from '@zillow/constellation-10';
import { useMainLayout } from '../../layouts/MainPageLayout/MainLayoutContext';
import { toggleCompId } from '../../../stores/appDataStore';
import { useAuth } from '../../../AuthProvider';
import {
  emitMarketCompsSelectCompetitorEvent,
  emitMarketCompsUnselectCompetitorEvent,
} from '../../../analytics/events';
import { type CompetitorMapDisplayMode, MapDot, type MapDotPayload } from '../../SharedComponents/Map/Dots/Dots';
import { OTHER_ADVERTISING_COLOR, PACKAGE_COLORS } from '../../SharedComponents/Map/Dots/Dots.consts';
import { useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { PageLoading } from '../../SharedComponents/PageLoading';
import {
  type ClientAdProductType,
  type ListingCompDto,
  type PortfolioAnalyticsPropertyResponseDto,
  type TopConstructionDto,
  TopConstructionStatus,
} from '@zg-rentals/ts-customer-analytics-client';
import { RadiusCircle } from './RadiusCircle';
import { MapLegend } from './MapLegend';

const MapComponent = dynamic(() => import('../../SharedComponents/Map/Map').then((mod) => mod.Map), {
  ssr: false,
  loading: () => <PageLoading />,
});

const getLatLonKey = (lat: number, lon: number) => `${lat}-${lon}`;

type MarketCompsMapWrapperProps = {
  selectedProperty: PortfolioAnalyticsPropertyResponseDto;
  comps: Array<ListingCompDto> | undefined;
  isLoading: boolean;
  error: Error | null;
  checkedCompIds: Set<string>;
  showAllComps: boolean;
  radius: number;
  showRadiusCircle: boolean;
  mapDisplayMode: CompetitorMapDisplayMode;
  viewsRankMap: Map<string, number>;
  leadsRankMap: Map<string, number>;
  newConstructions: Array<TopConstructionDto> | undefined;
  showNewConstruction: boolean;
  onRecenter: () => void;
};

export const MarketCompsMapWrapper = ({
  selectedProperty,
  comps,
  isLoading,
  error,
  checkedCompIds,
  showAllComps,
  radius,
  showRadiusCircle,
  mapDisplayMode,
  viewsRankMap,
  leadsRankMap,
  newConstructions,
  showNewConstruction,
  onRecenter,
}: MarketCompsMapWrapperProps) => {
  const { center, setCenter, bounds, setBounds, zoom, setZoom } = useMainLayout();
  const { user } = useAuth();

  useEffect(() => {
    const handleToggleClick = (e: MouseEvent) => {
      const button = (e.target as HTMLElement).closest('[data-action="toggle-comp"]');
      if (!button) return;

      e.preventDefault();
      e.stopPropagation();

      const compId = button.getAttribute('data-comp-id');
      if (!compId) return;

      const currentText = button.textContent?.trim();
      if (user) {
        if (currentText === 'Select this competitor') {
          emitMarketCompsSelectCompetitorEvent({ user });
        } else {
          emitMarketCompsUnselectCompetitorEvent({ user });
        }
      }

      toggleCompId(compId);

      if (currentText === 'Select this competitor') {
        button.textContent = 'Deselect this competitor';
      } else {
        button.textContent = 'Select this competitor';
      }
    };

    document.addEventListener('click', handleToggleClick, true);
    return () => document.removeEventListener('click', handleToggleClick, true);
  }, [user]);

  const allDots = useMemo(() => {
    const dots: Array<{ key: string; payload: MapDotPayload }> = [];
    const selectedListingAlias = selectedProperty.propertyDetails.listingAlias;

    comps?.forEach((comp, index) => {
      const compKey = getLatLonKey(comp.latitude, comp.longitude);
      const isSelectedProperty = comp.listingAlias === selectedListingAlias;
      const isChecked = checkedCompIds.has(comp.listingAlias);
      const isVisible = isSelectedProperty || showAllComps || isChecked;

      if (isVisible && !dots.some((dot) => dot.key === compKey)) {
        dots.push({
          key: compKey,
          payload: {
            type: 'competitorProperty',
            data: comp,
            isChecked,
            isSelectedProperty,
            isOffMarket: isSelectedProperty && selectedProperty.currentPublishedStatus === 'Inactive',
            bottomCircleColor:
              mapDisplayMode === 'default'
                ? comp.hasOtherProductType
                  ? OTHER_ADVERTISING_COLOR
                  : PACKAGE_COLORS[comp.currentProductType]
                : undefined,
            zIndex: isSelectedProperty ? 1000000 : 1000 - index,
            displayMode: mapDisplayMode,
            viewsRank: viewsRankMap.get(comp.listingAlias),
            leadsRank: leadsRankMap.get(comp.listingAlias),
          },
        });
      }
    });

    if (showNewConstruction) {
      newConstructions
        ?.filter((construction) => construction.constructionStatus !== TopConstructionStatus.COMPLETE)
        .forEach((construction) => {
          const key = `${getLatLonKey(construction.lat, construction.lon)}-newConstruction`;
          if (!dots.some((dot) => dot.key === key))
            dots.push({
              key,
              payload: { type: 'newConstruction', data: construction },
            });
        });
    }

    return dots;
  }, [
    selectedProperty.propertyDetails.listingAlias,
    selectedProperty.currentPublishedStatus,
    comps,
    checkedCompIds,
    showAllComps,
    mapDisplayMode,
    viewsRankMap,
    leadsRankMap,
    newConstructions,
    showNewConstruction,
  ]);

  const { visiblePackageTypes, hasOtherAdvertising } = useMemo(() => {
    const types = new Set<ClientAdProductType>();
    const selectedListingAlias = selectedProperty.propertyDetails.listingAlias;
    let hasOther = false;

    comps?.forEach((comp) => {
      if (comp.listingAlias === selectedListingAlias) return;
      const isChecked = checkedCompIds.has(comp.listingAlias);
      const isVisible = isChecked || showAllComps;
      if (!isVisible) return;

      if (comp.hasOtherProductType) {
        hasOther = true;
      } else if (comp.currentProductType) {
        types.add(comp.currentProductType);
      }
    });

    return { visiblePackageTypes: Array.from(types), hasOtherAdvertising: hasOther };
  }, [comps, checkedCompIds, showAllComps, selectedProperty.propertyDetails.listingAlias]);

  if (error) {
    return (
      <Box css={{ padding: '600' }}>
        <Heading level={5}>Map Error</Heading>
        <Paragraph css={{ marginTop: '400', color: 'red' }}>
          Failed to load competitor data. Please try again later.
        </Paragraph>
      </Box>
    );
  }

  return (
    <Box css={{ height: '100%', minHeight: '600px', position: 'relative', overflow: 'hidden', width: '100%' }}>
      {isLoading ? (
        <Box
          css={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            backgroundColor: 'gray.50',
          }}
        >
          <Spinner size="lg" />
        </Box>
      ) : (
        <Box css={{ position: 'absolute', inset: 0 }}>
          <MapComponent
            center={center}
            zoom={zoom}
            bounds={bounds}
            setCenter={setCenter}
            setZoom={setZoom}
            setBounds={setBounds}
          >
            {showRadiusCircle && (
              <RadiusCircle
                lat={selectedProperty.propertyDetails.lat}
                lng={selectedProperty.propertyDetails.lon}
                radiusMiles={radius}
                visible={showRadiusCircle}
              />
            )}
            {allDots.map(({ key, payload }: { key: string; payload: MapDotPayload }) => (
              <MapDot key={key} payload={payload} />
            ))}
          </MapComponent>
        </Box>
      )}
      <MapLegend
        displayMode={mapDisplayMode}
        showAllComps={showAllComps}
        showNewConstruction={showNewConstruction}
        onRecenter={onRecenter}
        visiblePackageTypes={visiblePackageTypes}
        hasOtherAdvertising={hasOtherAdvertising}
      />
    </Box>
  );
};
